import React, { useState } from 'react';

import { FloatingLabelInput } from '$partials/fields';
import { SimpleImage } from '$partials/media';
import Button from '$partials/ui/button.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/global.d.ts').Footer}
 */
const Footer = ({
    eyebrow,
    header,
    links,
    popularSearchesLinks,
    socialLinks,
    logo,
    copyrightText,
    complianceLinks,
    certificationLogos
}) => {
    return (
        <footer className='theme-brand bg-main-background text-main-text-primary relative'>
            <div className='layout-container layout-site-outer-padding py-32 px-16 sm:px-48 sm:py-40 md:px-60 md:py-48 flex flex-col gap-48'>
                {/* Newsletter Section */}
                {(eyebrow || header) && (
                    <div className='flex sm:flex-row flex-col gap-24 items-start sm:pb-32'>
                        <div className='flex-1 flex flex-col gap-16 pr-32 sm:max-w-full sm:pr-0'>
                            {eyebrow && (
                                <p className='type-style-eyebrow'>
                                    {eyebrow}
                                </p>
                            )}
                            {header && (
                                <h2 className='type-style-3 sm:type-style-2'>
                                    {header}
                                </h2>
                            )}
                        </div>
                        <div className='flex-1 flex flex-col w-full gap-24 md:pt-42'>
                            <form
                                action='https://beauchamp.us20.list-manage.com/subscribe/post?u=5a277c4f0ba8d7d38984cde12&amp;id=b96a91ae7f&amp;f_id=00af7ff1f0'
                                className='flex gap-8 w-full'
                                data-gtm-form='newsletter_subscription_form'
                                method='POST'
                                target='_blank'>
                                <div className='flex-1 flex flex-col justify-center relative'>
                                    <FloatingLabelInput
                                        required
                                        id='newsletter-email'
                                        label='EMAIL'
                                        type='email' />
                                </div>
                                <input
                                    name='b_5a277c4f0ba8d7d38984cde12_b96a91ae7f'
                                    type='hidden'
                                    value=''>
                                </input>
                                <Button
                                    as='button'
                                    className='button-primary-filled type-style-eyebrow'
                                    type='submit'>
                                    Submit
                                </Button>
                            </form>
                            {/* Social Links */}
                            {(socialLinks?.length > 0) && (
                                <div className='flex flex-wrap gap-24'>
                                    {socialLinks.map((social, i) => (
                                        <a
                                            {...social.link}
                                            aria-label={social.accessibleTitle}
                                            className='button-secondary-filled rounded w-[44px] h-[44px] flex items-center justify-center'
                                            key={i}>
                                            {social.socialIcon && (
                                                <SimpleImage
                                                    {...social.socialIcon.image}
                                                    className='w-[13px] h-[13px]'/>
                                            )}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Footer Links */}
                {(links?.length > 0) && (
                    <div className='col-wrapper grid grid-cols-2 md:grid-cols-4 gap-y-64 divide-main-border md:[&>*:nth-child(n+2)]:border-l'>
                        {links.map((linkSection, i) => (
                            <React.Fragment key={i}>
                                <div className='flex flex-col gap-16 items-start col-padding [&:nth-child(1)]:col-span-2 [&:nth-child(4)]:col-span-2 [&:nth-child(3)]:border-l md:[&:nth-child(1)]:col-span-1 md:[&:nth-child(4)]:col-span-1 md:[&:nth-child(3)]:border-l-0'>
                                    <p className='type-style-eyebrow'>
                                        {linkSection.heading}
                                    </p>
                                    <ul className='flex flex-col gap-8 w-full'>
                                        {linkSection.items.map((item, j) => (
                                            <li key={j}>
                                                <a
                                                    {...item.link}
                                                    className='type-style-body-l'>
                                                    {item.label}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                )}

                {/* Popular Searches */}
                {popularSearchesLinks?.length > 0 && (
                    <div className='flex flex-col gap-16 w-full'>
                        <p className='type-style-eyebrow'>
                            Popular Searches
                        </p>

                        <div className='col-wrapper grid sm:grid-cols-2 md:grid-cols-4 gap-y-20 sm:gap-y-64 divide-main-border sm:[&>*:nth-child(2n)]:border-l md:[&>*:nth-child(n+2)]:border-l'>
                            {Array.from(
                                { length: Math.ceil(popularSearchesLinks.length / 3) },
                                (_, colIndex) => {
                                    const columnLinks = popularSearchesLinks.slice(
                                        colIndex * 3,
                                        colIndex * 3 + 3
                                    );

                                    return (
                                        <React.Fragment key={colIndex}>
                                            <ul className='flex flex-col gap-8 col-padding'>
                                                {columnLinks.map((item, j) => (
                                                    <li key={j}>
                                                        <a
                                                            {...item.link}
                                                            className='type-style-body-r'>
                                                            {item.label}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </React.Fragment>
                                    );
                                }
                            )}
                        </div>
                    </div>
                )}


                {/* Logo */}
                {logo && (
                    <a
                        aria-label='Homepage'
                        href='/'>
                        <SimpleImage
                            className='w-[257px]'
                            {...logo} />
                    </a>
                )}

                {/* Legal Section */}
                <div className='border-t border-main-border pt-32 flex flex-col gap-30'>
                    <div className='flex md:flex-row flex-col sm:gap-y-40 md:gap-[80px] items-baseline'>
                        {copyrightText && (
                            <p className='type-style-eyebrow text-main-text-tertiary m-0'>
                                {copyrightText}
                            </p>
                        )}
                        {(complianceLinks?.length > 0) && (
                            <ul className='flex flex-col sm:flex-row sm:flex-wrap gap-y-24 sm:gap-40 list-none mt-16 sm:m-0 p-0 items-baseline'>
                                {complianceLinks.map((item, i) => (
                                    <li
                                        className='m-0 p-0 inline-flex'
                                        key={i}>
                                        <a
                                            {...item.link}
                                            className='type-style-body-r sm:type-style-eyebrow text-main-text-secondary'>
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {(certificationLogos?.length > 0) && (
                        <div className='flex flex-wrap gap-8 h-[32px]'>
                            {certificationLogos.map((cert, i) => (
                                cert.socialIcon && (
                                    <div
                                        className='h-[18px] w-[50px]'
                                        key={i}>
                                        <SimpleImage
                                            {...cert.socialIcon.image}
                                            className='h-18 w-50'/>
                                    </div>
                                )
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </footer>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default Footer;
